import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  // Use full API URL from env when available so requests go directly to backend
  // (prevents CRA dev server from responding "Cannot POST /api/...")
  baseUrl: process.env.REACT_APP_API_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const attendanceApi = createApi({
  reducerPath: 'attendanceApi',
  baseQuery,
  tagTypes: ['Attendance', 'Shift'],
  endpoints: (builder) => ({
    // Clock in/out
    clockIn: builder.mutation({
      query: (clockInData) => {
        console.log('🚀 RTK Query clockIn - Sending data:', JSON.stringify(clockInData, null, 2));
        return {
          url: '/attendance/clock-in',
          method: 'POST',
          body: clockInData,
        };
      },
      invalidatesTags: ['Attendance'],
    }),
    clockOut: builder.mutation({
      query: (clockOutData) => {
        console.log('🚀 RTK Query clockOut - Sending data:', JSON.stringify(clockOutData, null, 2));
        return {
          url: '/attendance/clock-out',
          method: 'POST',
          body: clockOutData,
        };
      },
      invalidatesTags: ['Attendance'],
    }),
    
    // Attendance records CRUD
    getAttendance: builder.query({
      query: (params = {}) => ({
        url: '/attendance',
        params,
      }),
      providesTags: ['Attendance'],
    }),
    createAttendance: builder.mutation({
      query: (attendanceData) => ({
        url: '/attendance',
        method: 'POST',
        body: attendanceData,
      }),
      invalidatesTags: ['Attendance'],
    }),
    updateAttendance: builder.mutation({
      query: ({ id, ...attendanceData }) => ({
        url: `/attendance/${id}`,
        method: 'PUT',
        body: attendanceData,
      }),
      invalidatesTags: ['Attendance'],
    }),
    deleteAttendance: builder.mutation({
      query: (id) => ({
        url: `/attendance/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Attendance'],
    }),
    getAttendanceHistory: builder.query({
      query: (params) => ({
        url: '/attendance/history',
        params,
      }),
      providesTags: ['Attendance'],
    }),
    getMyAttendance: builder.query({
      query: () => '/attendance/my-attendance',
      providesTags: ['Attendance'],
    }),
    getAttendanceSummary: builder.query({
      query: (params) => ({
        url: '/attendance/summary',
        params,
      }),
      providesTags: ['Attendance'],
    }),
    getAttendanceStats: builder.query({
      query: (params) => ({
        url: '/attendance/stats',
        params,
      }),
      providesTags: ['Attendance'],
    }),
    
    // Attendance corrections
    requestAttendanceCorrection: builder.mutation({
      query: (correctionData) => ({
        url: '/attendance/corrections',
        method: 'POST',
        body: correctionData,
      }),
      invalidatesTags: ['Attendance'],
    }),
    approveAttendanceCorrection: builder.mutation({
      query: ({ id, remarks }) => ({
        url: `/attendance/corrections/${id}/approve`,
        method: 'PATCH',
        body: { remarks },
      }),
      invalidatesTags: ['Attendance'],
    }),
    rejectAttendanceCorrection: builder.mutation({
      query: ({ id, remarks }) => ({
        url: `/attendance/corrections/${id}/reject`,
        method: 'PATCH',
        body: { remarks },
      }),
      invalidatesTags: ['Attendance'],
    }),
    
    // Shift management
    getShifts: builder.query({
      query: () => '/attendance/shifts',
      providesTags: ['Shift'],
    }),
    createShift: builder.mutation({
      query: (shiftData) => ({
        url: '/attendance/shifts',
        method: 'POST',
        body: shiftData,
      }),
      invalidatesTags: ['Shift'],
    }),
    updateShift: builder.mutation({
      query: ({ id, ...shiftData }) => ({
        url: `/attendance/shifts/${id}`,
        method: 'PUT',
        body: shiftData,
      }),
      invalidatesTags: ['Shift'],
    }),
    deleteShift: builder.mutation({
      query: (id) => ({
        url: `/attendance/shifts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Shift'],
    }),
    
    // Roster management
    getRoster: builder.query({
      query: (params) => ({
        url: '/attendance/roster',
        params,
      }),
      providesTags: ['Attendance'],
    }),
    updateRoster: builder.mutation({
      query: (rosterData) => ({
        url: '/attendance/roster',
        method: 'PUT',
        body: rosterData,
      }),
      invalidatesTags: ['Attendance'],
    }),
    
    // Reports
    getAttendanceReport: builder.query({
      query: (params) => ({
        url: '/attendance/reports',
        params,
      }),
      providesTags: ['Attendance'],
    }),
    exportAttendanceReport: builder.mutation({
      query: (params) => ({
        url: '/attendance/reports/export',
        method: 'POST',
        body: params,
      }),
    }),
  }),
});

export const {
  useClockInMutation,
  useClockOutMutation,
  useGetAttendanceQuery,
  useCreateAttendanceMutation,
  useUpdateAttendanceMutation,
  useDeleteAttendanceMutation,
  useGetDailyAttendanceQuery,
  useGetAttendanceHistoryQuery,
  useGetMyAttendanceQuery,
  useGetAttendanceSummaryQuery,
  useGetAttendanceStatsQuery,
  useRequestAttendanceCorrectionMutation,
  useApproveAttendanceCorrectionMutation,
  useRejectAttendanceCorrectionMutation,
  useGetShiftsQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useGetRosterQuery,
  useUpdateRosterMutation,
  useGetAttendanceReportQuery,
  useExportAttendanceReportMutation,
} = attendanceApi;
